export const CHECKOUT_RESUME_TOKEN_KEY_PREFIX = 'edx.checkout.resumeToken.';
export const CHECKOUT_STRIPE_PUBLISHABLE_KEY_KEY =
  'edx.checkout.stripePublishableKey';

// localStorage (not sessionStorage) is required here: /payment-return can
// load in a tab that only reaches our origin partway through a redirect
// chain through a third-party origin (observed for UPI QR/redirect flows).
// sessionStorage is only copied into a new tab at the moment that tab is
// created, so a tab that arrives at our origin later never sees state
// written afterward by the original checkout tab. localStorage has no such
// snapshot-at-creation limitation — every same-origin tab reads the same
// live store.
const canUseLocalStorage = (): boolean => {
  try {
    // Merely accessing window.localStorage can throw (e.g. some browsers'
    // privacy modes or storage-blocking settings), not just calling its
    // methods, so this check itself needs a try/catch.
    return (
      typeof window !== 'undefined' &&
      typeof window.localStorage !== 'undefined'
    );
  } catch {
    return false;
  }
};

const withLocalStorage = <T>(readWrite: () => T, fallback: T): T => {
  if (!canUseLocalStorage()) {
    return fallback;
  }

  try {
    return readWrite();
  } catch {
    return fallback;
  }
};

// The resume token is keyed by PaymentIntent id, not stored under a single
// shared key. localStorage is shared across every same-origin tab, so a
// single shared key would let concurrent checkout attempts in different
// tabs (e.g. two carts, or two payment methods) overwrite each other's
// token — the /payment-return tab for one attempt could then read another
// attempt's token and finalize/release the wrong PaymentIntent. Keying by
// PaymentIntent id (which /payment-return always has, from its own
// `payment_intent` query param) gives each attempt an isolated slot while
// still being visible across a cross-origin redirect chain.
const resumeTokenKey = (paymentIntentId: string): string =>
  `${CHECKOUT_RESUME_TOKEN_KEY_PREFIX}${paymentIntentId}`;

export const storeCheckoutResumeToken = (
  paymentIntentId: string,
  resumeToken: string,
): void => {
  withLocalStorage(() => {
    window.localStorage.setItem(resumeTokenKey(paymentIntentId), resumeToken);
  }, undefined);
};

export const readCheckoutResumeToken = (
  paymentIntentId: string,
): string | null =>
  withLocalStorage(
    () => window.localStorage.getItem(resumeTokenKey(paymentIntentId)),
    null,
  );

export const clearCheckoutResumeToken = (
  paymentIntentId: string,
  expectedResumeToken?: string,
): void => {
  withLocalStorage(() => {
    const key = resumeTokenKey(paymentIntentId);
    if (
      expectedResumeToken !== undefined &&
      window.localStorage.getItem(key) !== expectedResumeToken
    ) {
      // Belt-and-suspenders: keying by PaymentIntent id already isolates
      // concurrent attempts for different PaymentIntents. This guards the
      // unlikely case of two attempts sharing the same PaymentIntent id
      // (e.g. a retry that reuses it) racing on the same key.
      return;
    }
    window.localStorage.removeItem(key);
  }, undefined);
};

export const storeStripePublishableKey = (publishableKey: string): void => {
  if (!publishableKey) {
    return;
  }
  withLocalStorage(() => {
    window.localStorage.setItem(
      CHECKOUT_STRIPE_PUBLISHABLE_KEY_KEY,
      publishableKey,
    );
  }, undefined);
};

export const readStripePublishableKey = (): string | null =>
  withLocalStorage(
    () => window.localStorage.getItem(CHECKOUT_STRIPE_PUBLISHABLE_KEY_KEY),
    null,
  );

// Exposed for completeness/tests. Not called by clearCheckoutReturnSession
// (see the comment there) — nothing in the checkout flow currently needs
// to remove the publishable key outside of this file's own tests.
export const clearStripePublishableKey = (): void => {
  withLocalStorage(() => {
    window.localStorage.removeItem(CHECKOUT_STRIPE_PUBLISHABLE_KEY_KEY);
  }, undefined);
};

// Only the resume token is cleared here. The publishable key is a public,
// non-sensitive Stripe credential (safe to expose client-side by design)
// that is populated once, on mount, by each variant's StripeFormProvider —
// that effect is keyed on a stable prop and does not re-run on a retry. If
// this function cleared the publishable key after the first attempt, no
// code path would ever re-populate it, and every retry's /payment-return
// tab would load with a valid resume token but no way to initialize
// Stripe.js. There is no correctness or security reason to clear a public
// key eagerly, so it's simply left in localStorage indefinitely (it gets
// overwritten, not appended to, the next time a StripeFormProvider mounts).
export const clearCheckoutReturnSession = (
  paymentIntentId: string,
  expectedResumeToken?: string,
): void => {
  clearCheckoutResumeToken(paymentIntentId, expectedResumeToken);
};
